/**
 * Ref: https://github.com/WiaczeslawP/react-screen-keyboard
 */

import React, { PureComponent } from "react";
import PropTypes from "prop-types";
import KeyboardButton from "./KeyboardButton";

import LatinLayout from "./layouts/LatinLayout";
import CyrillicLayout from "./layouts/CyrillicLayout";
import SlovakLayout from "./layouts/SlovakLayout";
import UkrainianLayout from "./layouts/UkrainianLayout";
import CzechLayout from "./layouts/CzechLayout";
import PolishLayout from "./layouts/PolishLayout";
import SpanishLayout from "./layouts/SpanishLayout";
import GermanLayout from "./layouts/GermanLayout";
import FrenchLayout from "./layouts/FrenchLayout";
import ItalianLayout from "./layouts/ItalianLayout";
import PortugueseLayout from "./layouts/PortugueseLayout";
import DanishLayout from "./layouts/DanishLayout";
import NorwegianLayout from "./layouts/NorwegianLayout";
import SwedishLayout from "./layouts/SwedishLayout";
import FinnishLayout from "./layouts/FinnishLayout";
import DutchLayout from "./layouts/DutchLayout";
import LatvianLayout from "./layouts/LatvianLayout";
import SymbolsNumericReducedLayout from "./layouts/SymbolsNumericReducedLayout";
import NumericLayout from "./layouts/NumericLayout";
import CalculatorLayout from "./layouts/CalculatorLayout";
import SisuLatinLayout from "./layouts/SisuLatinLayout";

import BackspaceIcon from "./icons/BackspaceIcon";
import LanguageIcon from "./icons/LanguageIcon";
import ShiftIcon from "./icons/ShiftIcon";
import ShiftIconFilled from "./icons/ShiftIconFilled";
import theme from "../../../themes";
import { KeyboardNumericRoot, KeyboardRoot, KeyboardRow } from "./KeyboardStyles";
import { withTranslation } from "react-i18next";

const numbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 0];
const Style = {
    genericTouch: "genericTouch",
    sisu: "sisu",
    sisuEmail: "sisuEmail"
};

class Keyboard extends PureComponent {
    // Keyboard layer constants
    static KeyboardLayer = {
        ALPHABETIC: "alphabetic",
        SYMBOLS: "symbols",
        SPECIAL_KEYS: "specialKeys"
    };

    // Layout constants
    static SPECIAL_KEYS_PER_ROW = 9;

    // Direct mapping from language codes to keyboard layouts
    // Supports both new ISO codes (en-GB, de-DE) and legacy codes (PL, cz)
    // TODO: remove legacy codes once the migration is complete
    static LAYOUTS = new Map([
        // English
        ["en-GB", LatinLayout],

        // German
        ["de-DE", GermanLayout],
        ["de-CH", GermanLayout], // Swiss German uses same layout
        ["DE", GermanLayout], // Legacy code
        ["CH", GermanLayout], // Legacy code

        // French
        ["fr-FR", FrenchLayout],
        ["fr", FrenchLayout], // Legacy code

        // Spanish
        ["es-ES", SpanishLayout],
        ["es", SpanishLayout], // Legacy code

        // Italian
        ["it-IT", ItalianLayout],
        ["IT", ItalianLayout], // Legacy code

        // Portuguese
        ["pt-PT", PortugueseLayout],

        // Nordic languages
        ["da-DK", DanishLayout],
        ["dk", DanishLayout], // Legacy code
        ["nn-NO", NorwegianLayout],
        ["no", NorwegianLayout], // Legacy code
        ["sv-SE", SwedishLayout],
        ["se", SwedishLayout], // Legacy code
        ["fi-FI", FinnishLayout],
        ["fi", FinnishLayout], // Legacy code

        // Dutch/Flemish
        ["nl-NL", DutchLayout],
        ["nl-BE", DutchLayout], // Flemish uses Dutch layout
        ["nl", DutchLayout], // Legacy code
        ["be", DutchLayout], // Legacy code

        // Eastern European
        ["pl-PL", PolishLayout],
        ["PL", PolishLayout], // Legacy code
        ["cs-CZ", CzechLayout],
        ["cz", CzechLayout], // Legacy code
        ["sk-SK", SlovakLayout],
        ["sk", SlovakLayout], // Legacy code
        ["lv-LV", LatvianLayout],
        ["LV", LatvianLayout], // Legacy code
        ["uk-UA", UkrainianLayout],
        ["uk", UkrainianLayout] // Legacy code
    ]);

    // Event constants
    static PHYSICAL_ENTER_KEY = "Enter";

    static propTypes = {
        leftButtons: PropTypes.arrayOf(PropTypes.node),
        rightButtons: PropTypes.arrayOf(PropTypes.node),
        inputNode: PropTypes.any,
        onClick: PropTypes.func,
        onNext: PropTypes.func,
        nextEnabled: PropTypes.bool,
        isFirstLetterUppercase: PropTypes.bool,
        isNumeric: PropTypes.bool,
        isCalculator: PropTypes.bool,
        disableSymbols: PropTypes.bool,
        disableAlphabets: PropTypes.bool,
        style: PropTypes.string,
        maxWidth: PropTypes.string,
        margin: PropTypes.string,
        layouts: PropTypes.arrayOf(
            PropTypes.shape({
                symbolsKeyValue: PropTypes.string,
                layout: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)),
                specialKeysLayout: PropTypes.arrayOf(PropTypes.string),
                specialKeysLabel: PropTypes.string
            })
        ),
        symbolsLayout: PropTypes.shape({
            symbolsKeyValue: PropTypes.string,
            layout: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string))
        }),
        className: PropTypes.string,
        uppercaseOnly: PropTypes.bool,
        enableDecimal: PropTypes.bool,
        defaultEnterText: PropTypes.string,
        forceLatinLayout: PropTypes.bool,
        i18n: PropTypes.object,
        t: PropTypes.func
    };

    static defaultProps = {
        leftButtons: [],
        rightButtons: [],
        isFirstLetterUppercase: false,
        isNumeric: false,
        isCalculator: false,
        disableSymbols: false,
        disableAlphabets: false,
        maxWidth: "1020px",
        margin: "30px auto",
        layouts: [
            LatinLayout,
            SisuLatinLayout,
            SlovakLayout,
            UkrainianLayout,
            CyrillicLayout,
            CzechLayout,
            PolishLayout,
            SpanishLayout,
            GermanLayout,
            FrenchLayout,
            ItalianLayout,
            PortugueseLayout,
            DanishLayout,
            NorwegianLayout,
            SwedishLayout,
            FinnishLayout,
            DutchLayout,
            LatvianLayout
        ],
        symbolsLayout: SymbolsNumericReducedLayout,
        nextEnabled: true,
        uppercaseOnly: false,
        enableDecimal: false,
        defaultEnterText: "buttons.uc_next",
        forceLatinLayout: false
    };

    constructor(props) {
        super(props);

        this.isUppercase = this.isUppercase.bind(this);

        const { KeyboardLayer } = Keyboard;
        const currentLayout = this.getCurrentLayout(props.i18n.language, props.style);

        this.state = {
            currentLayout,
            // Single source of truth for which layer is showing
            currentLayer: props.disableAlphabets ? KeyboardLayer.SYMBOLS : KeyboardLayer.ALPHABETIC,
            // Evaluate this immediately so that a click of a key is not necessary
            uppercase: this.isUppercase()
        };

        // Enter key on the physical keyboard. Listen on `document`, not `#root`: some inputs
        // blur themselves on completion (e.g. the DATE_OF_BIRTH year field), moving focus to
        // <body> — which is an ancestor of #root, so a keypress there would never bubble into a
        // #root listener. document catches Enter regardless of which element holds focus.
        this.handlePhysicalKeyPressed = this.handlePhysicalKeyPressed.bind(this);
        document.addEventListener("keypress", this.handlePhysicalKeyPressed);
    }
    getCurrentLayout(language, style) {
        if (style === Style.sisuEmail) {
            return SisuLatinLayout;
        }

        // Some inputs are Latin-script by nature (e.g. postcodes) and must not adopt the
        // station's native script — a Cyrillic/Greek/etc. layout can never produce a valid
        // value. forceLatinLayout pins the alphabetic layer to Latin regardless of language.
        if (this.props.forceLatinLayout) {
            return LatinLayout;
        }

        return Keyboard.LAYOUTS.get(language) || LatinLayout;
    }

    componentDidUpdate(prevProps) {
        // Detect language changes and update keyboard layout accordingly
        if (prevProps.i18n.language !== this.props.i18n.language) {
            this.setState({
                currentLayout: this.getCurrentLayout(this.props.i18n.language, this.props.style),
                currentLayer: Keyboard.KeyboardLayer.ALPHABETIC // Reset to alphabetic on language change
            });
        }
    }

    componentWillUnmount() {
        document.removeEventListener("keypress", this.handlePhysicalKeyPressed);
    }

    handlePhysicalKeyPressed(e) {
        if (e.key === Keyboard.PHYSICAL_ENTER_KEY) {
            this.props.nextEnabled && this.props.onNext && this.props.inputNode
                ? this.props.onNext(this.props.inputNode.value)
                : null;
        }
    }

    // Helper to set keyboard layer
    setKeyboardLayer = layer => {
        this.setState({ currentLayer: layer });
        this.props.inputNode.focus();
    };

    handleLanguageClick = () => {
        this.setState({
            currentLayout: this.getCurrentLayout(this.props.i18n.language, this.props.style),
            currentLayer: Keyboard.KeyboardLayer.ALPHABETIC
        });
        this.props.inputNode.focus();
    };

    handleShiftClick = () => {
        this.setState({ uppercase: !this.state.uppercase });

        this.props.inputNode.focus();
    };

    handleSymbolsClick = () => {
        const { KeyboardLayer } = Keyboard;

        // From special keys layer, go back to alphabetic
        if (this.state.currentLayer === KeyboardLayer.SPECIAL_KEYS) {
            this.setKeyboardLayer(KeyboardLayer.ALPHABETIC);
            return;
        }

        // Toggle between alphabetic and symbols
        const nextLayer =
            this.state.currentLayer === KeyboardLayer.SYMBOLS ? KeyboardLayer.ALPHABETIC : KeyboardLayer.SYMBOLS;
        this.setKeyboardLayer(nextLayer);
    };

    handleSpecialKeysClick = () => {
        const { KeyboardLayer } = Keyboard;
        const nextLayer =
            this.state.currentLayer === KeyboardLayer.SPECIAL_KEYS
                ? KeyboardLayer.ALPHABETIC
                : KeyboardLayer.SPECIAL_KEYS;
        this.setKeyboardLayer(nextLayer);
    };

    handleShowSymbolsFromSpecialKeys = () => {
        this.setKeyboardLayer(Keyboard.KeyboardLayer.SYMBOLS);
    };

    handleLetterButtonClick = key => {
        key = key + ""; //stringfy or number keys (which send an int) break the selection point
        const { inputNode } = this.props;
        let { value, selectionStart, selectionEnd } = inputNode;
        const nextValue = value.substring(0, selectionStart) + key + value.substring(selectionEnd);

        inputNode.value = nextValue;
        if (this.props.onClick) {
            this.props.onClick(nextValue);
        }

        inputNode.focus();
        inputNode.setSelectionRange(selectionStart + key.length, selectionStart + key.length);

        this.setState({ uppercase: this.isUppercase() });
        inputNode.dispatchEvent(new Event("input", { bubbles: true }));
    };

    handleBackspaceClick = () => {
        const { inputNode } = this.props;
        const { value, selectionStart, selectionEnd } = inputNode;
        let nextValue;
        let nextSelectionPosition;
        if (selectionStart === selectionEnd) {
            nextValue = value.substring(0, selectionStart - 1) + value.substring(selectionEnd);
            nextSelectionPosition = selectionStart - 1;
        } else {
            nextValue = value.substring(0, selectionStart) + value.substring(selectionEnd);
            nextSelectionPosition = selectionStart;
        }
        nextSelectionPosition = nextSelectionPosition > 0 ? nextSelectionPosition : 0;

        inputNode.value = nextValue;
        if (this.props.onClick) {
            this.props.onClick(nextValue);
        }
        setTimeout(() => {
            inputNode.focus();
            inputNode.setSelectionRange(nextSelectionPosition, nextSelectionPosition);
        }, 0);
        this.setState({ uppercase: this.isUppercase() });
        inputNode.dispatchEvent(new Event("input", { bubbles: true }));
    };

    isUppercase() {
        const { inputNode, isFirstLetterUppercase, uppercaseOnly } = this.props;
        const upperCase = (inputNode && !inputNode.value.length && isFirstLetterUppercase) || uppercaseOnly;
        return inputNode && inputNode.type !== "password" && inputNode.dataset.type !== "email" && upperCase;
    }

    getKeys() {
        const { KeyboardLayer } = Keyboard;
        let keysSet;

        switch (this.state.currentLayer) {
            case KeyboardLayer.SPECIAL_KEYS:
                // Layer 3: Language-specific special characters (ą, ć, ę, etc.)
                keysSet = this.getSpecialKeysRows();
                break;

            case KeyboardLayer.SYMBOLS:
                // Layer 2: Numbers and symbols (.?123)
                keysSet = this.props.symbolsLayout.layout;
                break;

            case KeyboardLayer.ALPHABETIC:
            default:
                // Layer 1: Standard alphabetic layout (QWERTY/QWERTZ)
                keysSet = this.getCurrentLayout(this.props.i18n.language, this.props.style).layout;
                break;
        }

        return this.state.uppercase ? keysSet.map(keyRow => keyRow.map(key => key.toUpperCase())) : keysSet;
    }

    // Extract special keys row formatting
    getSpecialKeysRows() {
        // For email keyboards, use language-specific special keys
        let specialKeys;
        if (this.props.style === Style.sisuEmail) {
            specialKeys = this.getLanguageSpecialKeys() || [];
        } else {
            specialKeys = this.getSpecialKeysLayout() || [];
        }

        const rows = [];
        for (let i = 0; i < specialKeys.length; i += Keyboard.SPECIAL_KEYS_PER_ROW) {
            rows.push(specialKeys.slice(i, i + Keyboard.SPECIAL_KEYS_PER_ROW));
        }

        return rows;
    }

    getSpecialKeysLayout() {
        return this.getCurrentLayout(this.props.i18n.language, this.props.style).specialKeysLayout;
    }

    // Get language-specific special keys (for email keyboards to show special characters)
    getLanguageSpecialKeys() {
        const languageLayout = Keyboard.LAYOUTS.get(this.props.i18n.language) || LatinLayout;
        return languageLayout.specialKeysLayout;
    }

    hasSpecialKeys() {
        // Email keyboards: check if language has special characters
        if (this.props.style === Style.sisuEmail) {
            const languageKeys = this.getLanguageSpecialKeys();
            return languageKeys && languageKeys.length > 0;
        }

        // Regular keyboards: check layout special keys
        const specialKeys = this.getSpecialKeysLayout();
        return specialKeys && specialKeys.length > 0;
    }

    getSpecialKeysLabel() {
        // For email keyboards, get label from language-specific layout
        let layout;
        if (this.props.style === Style.sisuEmail) {
            layout = Keyboard.LAYOUTS.get(this.props.i18n.language) || LatinLayout;
        } else {
            layout = this.getCurrentLayout(this.props.i18n.language, this.props.style);
        }

        const specialKeysLabel = layout.specialKeysLabel || "Ąćę";
        const { uppercase } = this.state;

        return uppercase ? specialKeysLabel.toUpperCase() : specialKeysLabel.toLowerCase();
    }

    getSymbolsKeyValue() {
        const { KeyboardLayer } = Keyboard;
        const { uppercase } = this.state;

        // Button label shows what you'll GET when you press it
        if (this.state.currentLayer === KeyboardLayer.SYMBOLS) {
            const symbolsKeyValue =
                this.getCurrentLayout(this.props.i18n.language, this.props.style).symbolsKeyValue || "Abc";
            return uppercase ? symbolsKeyValue.toUpperCase() : symbolsKeyValue.toLowerCase();
        }

        // If in special keys layer, show "Abc" to go back to alphabetic
        if (this.state.currentLayer === KeyboardLayer.SPECIAL_KEYS) {
            const symbolsKeyValue =
                this.getCurrentLayout(this.props.i18n.language, this.props.style).symbolsKeyValue || "Abc";
            return uppercase ? symbolsKeyValue.toUpperCase() : symbolsKeyValue.toLowerCase();
        }

        // Currently showing alphabetic, button shows ".?123" to go to symbols
        const symbolsKeyValue = this.props.symbolsLayout.symbolsKeyValue || ".?123";
        return uppercase ? symbolsKeyValue.toUpperCase() : symbolsKeyValue.toLowerCase();
    }

    renderKeyRows() {
        const keys = this.getKeys();
        const { uppercase } = this.state;

        return keys.map((row, i) => (
            <KeyboardRow key={`row-${i}`}>
                {i === keys.length - 1 && (
                    <KeyboardButton
                        // if is uppercase, show active shift icon
                        value={uppercase ? <ShiftIconFilled /> : <ShiftIcon />}
                        classes="keyboard-shiftButton"
                        onClick={this.handleShiftClick}
                    />
                )}
                {row.map(button => (
                    <KeyboardButton value={button} onClick={this.handleLetterButtonClick} key={button} />
                ))}
                {i === keys.length - 1 && (
                    <KeyboardButton
                        value={this.getSymbolsKeyValue()}
                        classes="keyboard-symbolButton"
                        onClick={this.handleSymbolsClick}
                    />
                )}
            </KeyboardRow>
        ));
    }

    renderSisuKeyRows() {
        const keys = this.getKeys();
        const { uppercase } = this.state;

        return keys.map((row, i) => (
            <KeyboardRow key={`row-${i}`}>
                {i === keys.length - 1 && !this.state.showSymbols && (
                    <KeyboardButton
                        value={uppercase ? <ShiftIconFilled /> : <ShiftIcon />}
                        onClick={this.handleShiftClick}
                        minWidth={"100px"}
                    />
                )}
                {row.map(button => {
                    const isNumber = !isNaN(button);
                    const isSymbol = this.state.showSymbols && !isNumber;
                    const showDot = this.state.showSymbols && button === "." && this.props.enableDecimal;
                    const disabled = !showDot && isSymbol && this.props.disableSymbols;

                    return (
                        <KeyboardButton
                            value={button}
                            onClick={this.handleLetterButtonClick}
                            key={button}
                            isDisabled={disabled}
                        />
                    );
                })}
                {i === 0 && <KeyboardButton value={<BackspaceIcon />} onPressed={this.handleBackspaceClick} />}
                {i === 1 && (
                    <KeyboardButton
                        value={this.props.t(this.props.defaultEnterText)}
                        onClick={() =>
                            this.props.nextEnabled && this.props.onNext
                                ? this.props.onNext(this.props.inputNode.value)
                                : null
                        }
                        backgroundColor={
                            this.props.nextEnabled ? theme.colours.flat.blue.hex : theme.colours.flat.gray.hex
                        }
                        color={theme.colours.flat.white.hex}
                        minWidth={"150px"}
                        isDisabled={!this.props.nextEnabled}
                    />
                )}
            </KeyboardRow>
        ));
    }

    renderSisuSpecialKeysRow() {
        const { KeyboardLayer } = Keyboard;

        // Layer 2: Symbols view
        if (this.state.currentLayer === KeyboardLayer.SYMBOLS) {
            return (
                <KeyboardRow>
                    <KeyboardButton
                        value={this.getSymbolsKeyValue()}
                        onClick={this.handleSymbolsClick}
                        isDisabled={this.props.disableAlphabets}
                    />
                    <KeyboardButton
                        value={" "}
                        classes="keyboard-spaceButton"
                        onClick={this.handleLetterButtonClick}
                        isDisabled={this.props.style === Style.sisuEmail}
                    />
                    {this.renderSpecialKeysToggleButton()}
                </KeyboardRow>
            );
        }

        // Layer 3: Special keys view
        if (this.state.currentLayer === KeyboardLayer.SPECIAL_KEYS) {
            return (
                <KeyboardRow>
                    <KeyboardButton
                        value={this.props.symbolsLayout.symbolsKeyValue}
                        onClick={this.handleShowSymbolsFromSpecialKeys}
                    />
                    <KeyboardButton value={" "} classes="keyboard-spaceButton" onClick={this.handleLetterButtonClick} />
                    <KeyboardButton value={this.getSymbolsKeyValue()} onClick={this.handleSymbolsClick} />
                </KeyboardRow>
            );
        }

        // Layer 1: Alphabetic view (default)
        return this.renderAlphabeticBottomRow();
    }

    // Extract repeated special keys toggle button rendering
    renderSpecialKeysToggleButton() {
        if (!this.hasSpecialKeys()) {
            // No special keys available, show duplicate symbols toggle button
            return (
                <KeyboardButton
                    value={this.getSymbolsKeyValue()}
                    onClick={this.handleSymbolsClick}
                    isDisabled={this.props.disableAlphabets}
                />
            );
        }

        // Show special keys toggle button
        return (
            <KeyboardButton
                value={this.getSpecialKeysLabel()}
                onClick={this.handleSpecialKeysClick}
                isDisabled={this.props.disableAlphabets}
            />
        );
    }

    renderAlphabeticBottomRow() {
        let keys = this.getSpecialKeysLayout() || [];
        const hasLanguageSpecialKeys = this.hasSpecialKeys();
        if (hasLanguageSpecialKeys) {
            keys = keys.filter(key => key !== "@hotmail.com");
        }
        let layout;

        switch (this.props.style) {
            case Style.sisuEmail:
                // Email keyboards: show email shortcuts + optional language special keys button
                layout = (
                    <KeyboardRow>
                        <KeyboardButton value={this.getSymbolsKeyValue()} onClick={this.handleSymbolsClick} />
                        {keys.map(button => this.renderEmailShortcutButton(button))}
                        {hasLanguageSpecialKeys && this.renderSpecialKeysToggleButton()}
                    </KeyboardRow>
                );

                break;

            case Style.genericTouch:
            case Style.sisu:
            default:
                layout = (
                    <KeyboardRow>
                        <KeyboardButton value={this.getSymbolsKeyValue()} onClick={this.handleSymbolsClick} />
                        <KeyboardButton
                            value={" "}
                            classes="keyboard-spaceButton"
                            onClick={this.handleLetterButtonClick}
                        />
                        {this.renderSpecialKeysToggleButton()}
                    </KeyboardRow>
                );
                break;
        }

        return layout;
    }

    renderEmailShortcutButton(button) {
        const isAtSymbol = button === "@";
        const isDotCom = button === ".com";
        const classes = isAtSymbol || isDotCom ? undefined : "keyboard-emailKey";

        return <KeyboardButton value={button} onClick={this.handleLetterButtonClick} classes={classes} key={button} />;
    }

    renderNumeric() {
        const keys = NumericLayout.layout;
        const enableDecimal = this.props.enableDecimal;
        const { leftButtons, rightButtons } = this.props;
        const hasExtraButtons = leftButtons.length > 0 || rightButtons.length > 0;

        return (
            <KeyboardNumericRoot className={this.props.className}>
                {keys.map((row, i) => (
                    <KeyboardRow key={`row-${i}`}>
                        {row.map(button => (
                            <KeyboardButton value={button} onClick={this.handleLetterButtonClick} key={button} />
                        ))}
                    </KeyboardRow>
                ))}
                <KeyboardRow key="control-row">
                    {enableDecimal && (
                        <KeyboardButton key={"DecimalButton"} value={"."} onClick={this.handleLetterButtonClick} />
                    )}
                    <KeyboardButton value={<BackspaceIcon />} onClick={this.handleBackspaceClick} />
                </KeyboardRow>
                {hasExtraButtons && (
                    <KeyboardRow>
                        {leftButtons}
                        {rightButtons}
                    </KeyboardRow>
                )}
            </KeyboardNumericRoot>
        );
    }

    renderCalculator() {
        const keys = CalculatorLayout.layout;
        const enableDecimal = this.props.enableDecimal;
        const { leftButtons, rightButtons } = this.props;
        const hasExtraButtons = leftButtons.length > 0 || rightButtons.length > 0;

        return (
            <KeyboardNumericRoot className={this.props.className}>
                {keys.map((row, i) => (
                    <KeyboardRow key={`row-${i}`}>
                        {row.map(button => (
                            <KeyboardButton value={button} onClick={this.handleLetterButtonClick} key={button} />
                        ))}
                    </KeyboardRow>
                ))}
                <KeyboardRow key="control-row">
                    {enableDecimal && (
                        <KeyboardButton key={"DecimalButton"} value={"."} onClick={this.handleLetterButtonClick} />
                    )}
                    <KeyboardButton value={<BackspaceIcon />} onClick={this.handleBackspaceClick} />
                </KeyboardRow>
                {hasExtraButtons && (
                    <KeyboardRow>
                        {leftButtons}
                        {rightButtons}
                    </KeyboardRow>
                )}
            </KeyboardNumericRoot>
        );
    }

    renderSisuNumpad() {
        const keys = NumericLayout.layout;
        const { enableDecimal } = this.props;
        const { leftButtons, rightButtons } = this.props;
        const hasExtraButtons = leftButtons.length > 0 || rightButtons.length > 0;

        return (
            <KeyboardNumericRoot className={this.props.className}>
                {keys.map((row, i) => (
                    <KeyboardRow key={`row-${i}`}>
                        {row.map(button => (
                            <KeyboardButton key={button} value={button} onClick={this.handleLetterButtonClick} />
                        ))}
                    </KeyboardRow>
                ))}
                <KeyboardRow key="control-row">
                    {enableDecimal && (
                        <KeyboardButton key={"DecimalButton"} value={"."} onClick={this.handleLetterButtonClick} />
                    )}
                    <KeyboardButton
                        key={"BackspaceButton"}
                        value={<BackspaceIcon />}
                        onClick={this.handleBackspaceClick}
                    />
                    <KeyboardButton
                        key={"NextButton"}
                        value={this.props.t(this.props.defaultEnterText)}
                        onClick={() =>
                            this.props.nextEnabled && this.props.onNext
                                ? this.props.onNext(this.props.inputNode.value)
                                : null
                        }
                        backgroundColor={
                            this.props.nextEnabled ? theme.colours.flat.blue.hex : theme.colours.flat.gray.hex
                        }
                        color={theme.colours.flat.white.hex}
                        classes={"NextButton"}
                        minWidth={"150px"}
                        isDisabled={!this.props.nextEnabled}
                    />
                </KeyboardRow>
                {hasExtraButtons && (
                    <KeyboardRow>
                        {leftButtons}
                        {rightButtons}
                    </KeyboardRow>
                )}
            </KeyboardNumericRoot>
        );
    }

    renderAlphanumeric() {
        const { leftButtons, rightButtons, inputNode } = this.props;
        return (
            <KeyboardRoot maxWidth={this.props.maxWidth} margin={this.props.margin} className={this.props.className}>
                <KeyboardRow>
                    {numbers.map(button => (
                        <KeyboardButton
                            value={button}
                            onClick={this.handleLetterButtonClick}
                            classes="keyboard-numberButton"
                            key={button}
                        />
                    ))}
                    <KeyboardButton
                        value={<BackspaceIcon />}
                        classes="keyboard-backspaceButton"
                        onClick={this.handleBackspaceClick}
                    />
                </KeyboardRow>
                {this.renderKeyRows()}
                <KeyboardRow>
                    {leftButtons}
                    <KeyboardButton
                        value={<LanguageIcon />}
                        classes="keyboard-languageButton"
                        onClick={this.handleLanguageClick}
                    />
                    {inputNode.dataset.type === "email" ? (
                        <KeyboardButton
                            value={"@"}
                            classes="keyboard-atButton"
                            onClick={this.handleLetterButtonClick}
                        />
                    ) : null}
                    <KeyboardButton value={" "} classes="keyboard-spaceButton" onClick={this.handleLetterButtonClick} />
                    {inputNode.dataset.type === "email" ? (
                        <KeyboardButton
                            value={"."}
                            classes="keyboard-fullstopButton"
                            onClick={this.handleLetterButtonClick}
                        />
                    ) : null}
                    {rightButtons}
                </KeyboardRow>
            </KeyboardRoot>
        );
    }

    renderSisuAlphanumeric() {
        return (
            <KeyboardRoot maxWidth={this.props.maxWidth} margin={this.props.margin} className={this.props.className}>
                {this.renderSisuKeyRows()}
                {this.renderSisuSpecialKeysRow()}
            </KeyboardRoot>
        );
    }

    render() {
        if (!this.props.inputNode) {
            return null;
        }

        switch (this.props.style) {
            case Style.genericTouch:
                return this.props.isNumeric ? this.renderNumeric() : this.renderAlphanumeric();

            case Style.sisu:
                if (this.props.isNumeric) {
                    if (this.props.isCalculator) {
                        return this.renderCalculator();
                    } else {
                        return this.renderSisuNumpad();
                    }
                } else {
                    return this.renderSisuAlphanumeric();
                }

            case Style.sisuEmail:
                return this.props.isNumeric ? this.renderSisuNumpad() : this.renderSisuAlphanumeric();

            default:
                return this.props.isNumeric ? this.renderNumeric() : this.renderAlphanumeric();
        }
    }
}

export default withTranslation()(Keyboard);
export { Style };
